import React, { useState, useRef, useEffect, useCallback } from 'react';
import './HistoryTimes.css';
import { db, storage } from '../firebase';
import { useAuth } from './Auth/AuthProvider';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { LoadingSpinner } from './Spinner';

export default function HistoryTimes() {
  const { user, userProfileData } = useAuth();
  const [showCarousel, setShowCarousel] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const fileInputRef = useRef();
  const [imageLoaded, setImageLoaded] = useState({});
  const [avatarError, setAvatarError] = useState({});
  const [deleting, setDeleting] = useState(false);

  // Referencias para el carrusel
  const carouselRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [animationId, setAnimationId] = useState(null);

  // Cargar historias
  useEffect(() => {
    const fetchStories = async () => {
      if (!user) {
        setStories([]);
        setLoadingStories(false);
        return;
      }
      
      setLoadingStories(true);
      try {
        // 1. Obtener seguidos
        const followsSnap = await getDocs(query(collection(db, 'follows'), where('follower', '==', user.uid)));
        const followingIds = followsSnap.docs.map(doc => doc.data().following);
        
        // 2. Obtener top10 usuarios
        let topUsers = [];
        try {
          const functions = getFunctions();
          const debugForceUpdate = httpsCallable(functions, 'debugForceUpdate');
          const result = await debugForceUpdate();
          const { success, debugInfo } = result.data;
          if (success && debugInfo && Array.isArray(debugInfo)) {
            topUsers = debugInfo
              .filter(u => u.routines > 0)
              .slice(0, 10)
              .map((u) => ({
                id: u.id,
                displayName: u.displayName || u.name || '',
                nickname: u.nickname || u.name || '',
                photoURL: u.photoURL || '',
                customPhotoURL: u.customPhotoURL || null
              }));
          }
        } catch (err) {
          console.warn('Error fetching top users:', err);
          topUsers = [];
        }
        
        // 3. Crear lista ordenada de IDs
        const ownId = user.uid;
        const allFollowingIds = Array.from(new Set([ownId, ...followingIds]));
        const top10ExtraIds = topUsers.map(u => u.id).filter(id => !allFollowingIds.includes(id));
        const orderedIds = [...allFollowingIds, ...top10ExtraIds];
        
        // 4. Obtener historias
        const storiesList = [];
        for (const uid of orderedIds) {
          const topUserInfo = topUsers.find(u => u.id === uid);
          const storyDoc = await getDoc(doc(db, 'stories', uid));
          
          if (storyDoc.exists()) {
            const data = storyDoc.data();
            storiesList.push({
              userId: uid,
              userName: data.userName || topUserInfo?.displayName || 'Usuario',
              userAvatar: data.userAvatar || topUserInfo?.customPhotoURL || topUserInfo?.photoURL || '',
              imageUrl: data.imageUrl || '',
              createdAt: data.createdAt,
              isOwn: uid === ownId,
              isTop10: !!topUserInfo,
              hasStory: !!(data.imageUrl)
            });
          } else if (uid === ownId) {
            // Siempre mostrar historia propia aunque esté vacía
            storiesList.push({
              userId: uid,
              userName: userProfileData?.nickname || userProfileData?.displayName || user.displayName || user.email || 'Tú',
              userAvatar: userProfileData?.customPhotoURL || userProfileData?.photoURL || user.photoURL || '',
              imageUrl: '',
              createdAt: null,
              isOwn: true,
              isTop10: false,
              hasStory: false
            });
          } else if (topUserInfo) {
            storiesList.push({
              userId: uid,
              userName: topUserInfo?.displayName || 'Usuario',
              userAvatar: topUserInfo?.customPhotoURL || topUserInfo?.photoURL || '',
              imageUrl: '',
              createdAt: null,
              isOwn: false,
              isTop10: true,
              hasStory: false
            });
          }
        }
        
        setStories(storiesList);
      } catch (err) {
        console.error('Error fetching stories:', err);
        setStories([]);
      } finally {
        setLoadingStories(false);
      }
    };
    
    fetchStories();
  }, [user, userProfileData]);

  // Efectos para logo
  useEffect(() => {
    if (showCarousel) {
      // Solo ocultar el logo de bienvenida, no el título
      window.dispatchEvent(new CustomEvent('hideRatafitLogo'));
    } else {
      window.dispatchEvent(new CustomEvent('showRatafitLogo'));
    }
  }, [showCarousel]);

  // Precargar imágenes
  useEffect(() => {
    if (!showCarousel || stories.length === 0) return;
    
    const preloadImage = (story) => {
      if (!story.imageUrl || imageLoaded[story.userId]) return;
      
      const img = new Image();
      img.onload = () => setImageLoaded(prev => ({ ...prev, [story.userId]: true }));
      img.onerror = () => setImageLoaded(prev => ({ ...prev, [story.userId]: false }));
      img.src = story.imageUrl;
    };

    // Precargar imagen actual y siguientes
    preloadImage(stories[currentStoryIndex]);
    if (currentStoryIndex + 1 < stories.length) {
      preloadImage(stories[currentStoryIndex + 1]);
    }
    if (currentStoryIndex - 1 >= 0) {
      preloadImage(stories[currentStoryIndex - 1]);
    }
  }, [showCarousel, currentStoryIndex, stories, imageLoaded]);

  // Navegación del carrusel
  const setSliderPosition = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.style.transform = `translateX(${currentTranslate}px)`;
    }
  }, [currentTranslate]);

  const animation = useCallback(() => {
    setSliderPosition();
    if (isDragging) {
      const id = requestAnimationFrame(animation);
      setAnimationId(id);
    }
  }, [setSliderPosition, isDragging]);

  const updateCarouselPosition = useCallback((index) => {
    if (!carouselRef.current || stories.length <= 1) return;
    
    const validIndex = Math.max(0, Math.min(index, stories.length - 1));
    const translateX = -(validIndex * window.innerWidth);
    
    carouselRef.current.style.transition = 'transform 0.3s ease-out';
    carouselRef.current.style.transform = `translateX(${translateX}px)`;
    
    setCurrentTranslate(translateX);
    setPrevTranslate(translateX);
  }, [stories.length]);

  const goToStory = useCallback((index) => {
    if (stories.length <= 1 || index === currentStoryIndex) return;
    if (index < 0 || index >= stories.length) return;
    
    setCurrentStoryIndex(index);
    updateCarouselPosition(index);
  }, [currentStoryIndex, stories.length, updateCarouselPosition]);

  // Manejo de touch/drag
  const handleDragStart = useCallback((e) => {
    if (stories.length <= 1) return;
    
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
    
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'none';
    }
    
    const id = requestAnimationFrame(animation);
    setAnimationId(id);
  }, [stories.length, animationId, animation]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || stories.length <= 1) return;
    
    e.preventDefault();
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const currentPosition = clientX - startX;
    
    setCurrentTranslate(prevTranslate + currentPosition);
  }, [isDragging, startX, prevTranslate, stories.length]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging || stories.length <= 1) return;
    
    setIsDragging(false);
    
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    
    const movedBy = currentTranslate - prevTranslate;
    const threshold = window.innerWidth / 4;
    
    if (movedBy < -threshold && currentStoryIndex < stories.length - 1) {
      // Swipe izquierda - siguiente
      setCurrentStoryIndex(prev => prev + 1);
    } else if (movedBy > threshold && currentStoryIndex > 0) {
      // Swipe derecha - anterior
      setCurrentStoryIndex(prev => prev - 1);
    }
    
    updateCarouselPosition(currentStoryIndex);
  }, [isDragging, animationId, currentTranslate, prevTranslate, currentStoryIndex, stories.length, updateCarouselPosition]);

  // Abrir carrusel
  const openCarousel = useCallback((index) => {
    setCurrentStoryIndex(index);
    setShowCarousel(true);
    
    // Inicializar posiciones
    const initialTranslate = -(index * window.innerWidth);
    setCurrentTranslate(initialTranslate);
    setPrevTranslate(initialTranslate);
    
    // Pequeño delay para asegurar que el DOM esté listo
    setTimeout(() => {
      if (carouselRef.current) {
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(${initialTranslate}px)`;
      }
    }, 50);
  }, []);

  // Cerrar carrusel
  const closeCarousel = useCallback(() => {
    setShowCarousel(false);
    setIsDragging(false);
    setCurrentTranslate(0);
    setPrevTranslate(0);
    
    if (animationId) {
      cancelAnimationFrame(animationId);
      setAnimationId(null);
    }
  }, [animationId]);

  // Subir historia
  const handleAddStoryClick = () => {
    if (!user || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    
    setUploading(true);
    setUploadError('');
    
    try {
      // Eliminar historia anterior si existe
      const prevStoryDoc = await getDoc(doc(db, 'stories', user.uid));
      if (prevStoryDoc.exists()) {
        const prevData = prevStoryDoc.data();
        if (prevData.imageUrl) {
          try {
            const url = new URL(prevData.imageUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+)$/);
            if (pathMatch?.[1]) {
              const storagePath = decodeURIComponent(pathMatch[1]);
              const prevImageRef = ref(storage, storagePath);
              await deleteObject(prevImageRef);
            }
          } catch (err) {
            console.warn('Error deleting previous image:', err);
          }
        }
      }
      
      // Subir nueva imagen
      const timestamp = Date.now();
      const storageRef = ref(storage, `stories/${user.uid}/${timestamp}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      
      const userName = userProfileData?.nickname || 
                      userProfileData?.displayName || 
                      user.displayName || 
                      user.email || 
                      "Usuario";
      
      const userAvatar = userProfileData?.customPhotoURL || 
                        userProfileData?.photoURL || 
                        user.photoURL || 
                        "";
      
      // Guardar en Firestore
      await setDoc(doc(db, 'stories', user.uid), {
        userId: user.uid,
        userName,
        userAvatar,
        imageUrl,
        createdAt: serverTimestamp(),
      });
      
      // Actualizar estado local
      setStories(prevStories => 
        prevStories.map(story => 
          story.isOwn 
            ? { ...story, imageUrl, hasStory: true }
            : story
        )
      );
      
      alert('¡Historia subida exitosamente!');
    } catch (err) {
      console.error('Error uploading story:', err);
      setUploadError('Error al subir la historia. Intenta de nuevo.');
    } finally {
      setUploading(false);
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Eliminar historia
  const handleDeleteOwnStory = async () => {
    if (!user || deleting) return;
    
    setDeleting(true);
    try {
      const storyDoc = await getDoc(doc(db, 'stories', user.uid));
      if (storyDoc.exists()) {
        const data = storyDoc.data();
        
        // Eliminar imagen de Storage
        if (data.imageUrl) {
          try {
            const url = new URL(data.imageUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+)$/);
            if (pathMatch?.[1]) {
              const storagePath = decodeURIComponent(pathMatch[1]);
              const imageRef = ref(storage, storagePath);
              await deleteObject(imageRef);
            }
          } catch (err) {
            console.warn('Error deleting image from storage:', err);
          }
        }
        
        // Eliminar documento
        await deleteDoc(doc(db, 'stories', user.uid));
      }
      
      // Actualizar estado local
      setStories(prevStories => 
        prevStories.map(story => 
          story.isOwn 
            ? { ...story, imageUrl: '', hasStory: false }
            : story
        )
      );
      
      setShowCarousel(false);
    } catch (err) {
      console.error('Error deleting story:', err);
      alert('Error al eliminar la historia. Intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  // Obtener imagen para botón de agregar
  const ownStory = stories.find(s => s.isOwn);
  const addBtnImage = (ownStory?.hasStory && ownStory?.imageUrl) 
    ? ownStory.imageUrl 
    : (userProfileData?.customPhotoURL || 
       userProfileData?.photoURL || 
       user?.photoURL || 
       '/default-avatar.png');

  return (
    <div className="history-times-navbar">
      <div className="history-times-list">
        {/* Botón agregar historia */}
        <button 
          className="history-times-add-btn" 
          onClick={handleAddStoryClick} 
          disabled={uploading}
          title="Subir historia"
        >
          <img 
            src={addBtnImage} 
            alt="Tu historia" 
            className="history-times-add-avatar"
            onError={(e) => { e.target.src = '/default-avatar.png'; }}
          />
          <span className="history-times-add-plus">+</span>
        </button>
        
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={uploading}
        />

        {/* Lista de historias */}
        {!loadingStories && stories.length === 0 && (
          <span className="history-times-empty">No hay historias aún</span>
        )}

        {stories.length > 0 && stories.map((story, idx) => {
          const avatarSrc = avatarError[story.userId]
            ? '/default-avatar.png'
            : (story.hasStory && story.imageUrl
                ? story.imageUrl
                : (story.userAvatar || '/default-avatar.png'));

          return (
            <button 
              key={story.userId} 
              className="history-times-story-btn" 
              onClick={() => openCarousel(idx)}
              title={story.isTop10 ? 'Usuario top 10' : undefined}
            >
              <div className="history-times-avatar-wrapper">
                {story.isTop10 && (
                  <div className="history-times-top-badge" title="Usuario destacado">
                    🏆
                  </div>
                )}
                <img
                  src={avatarSrc}
                  alt={story.userName}
                  className={`history-times-avatar ${!story.hasStory ? 'no-story' : ''}`}
                  onError={() => {
                    setAvatarError(prev => ({
                      ...prev,
                      [story.userId]: true,
                    }));
                  }}
                  title={story.isTop10 ? 'Usuario top 10' : undefined}
                />
              </div>
              <span className="history-times-username">{story.userName}</span>
            </button>
          );
        })}
      </div>

      {uploadError && (
        <div className="history-times-error">{uploadError}</div>
      )}

      {/* Modal del carrusel */}
      {showCarousel && stories.length > 0 && (
        <div className="history-times-modal" onClick={closeCarousel}>
          <div className="history-times-modal-content" onClick={e => e.stopPropagation()}>
            <button className="history-times-modal-close" onClick={closeCarousel}>
              ×
            </button>

            <div 
              className="history-times-carousel-container"
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
                              <div 
                className="history-times-carousel-track"
                ref={carouselRef}
                style={{ 
                  width: `${stories.length * 100}vw`,
                  transform: `translateX(-${currentStoryIndex * 100}vw)`
                }}
              >
                {stories.map((story, idx) => {
                  const isActive = idx === currentStoryIndex;
                  const hasImage = story.hasStory && story.imageUrl;
                  const imageSrc = hasImage && imageLoaded[story.userId] 
                    ? story.imageUrl 
                    : (story.userAvatar || '/default-avatar.png');

                  return (
                    <div key={story.userId} className="history-times-carousel-slide">
                      <div className="history-times-image-container">
                        <img
                          src={imageSrc}
                          alt="Historia"
                          className="history-times-modal-image"
                          style={{
                            filter: hasImage && !imageLoaded[story.userId] 
                              ? 'blur(8px) brightness(0.7)' 
                              : 'none',
                            opacity: !hasImage ? 0.8 : 1
                          }}
                          onLoad={() => {
                            if (hasImage) {
                              setImageLoaded(prev => ({ ...prev, [story.userId]: true }));
                            }
                          }}
                          onError={(e) => { 
                            e.target.src = story.userAvatar || '/default-avatar.png'; 
                          }}
                        />
                        
                        {hasImage && !imageLoaded[story.userId] && (
                          <div className="history-times-loading-spinner">
                            <div className="spinner"></div>
                          </div>
                        )}
                      </div>

                      <div className="history-times-modal-user">
                        <img 
                          src={avatarError[story.userId]
                            ? '/default-avatar.png'
                            : (story.userAvatar || '/default-avatar.png')}
                          alt={story.userName} 
                          className="history-times-modal-avatar"
                          onError={() => {
                            setAvatarError(prev => ({
                              ...prev,
                              [story.userId]: true,
                            }));
                          }}
                        />
                        <span className="history-times-modal-username">
                          {story.userName}
                        </span>
                        
                        {story.isOwn && story.hasStory && (
                          <button
                            className="history-times-delete-btn"
                            onClick={handleDeleteOwnStory}
                            disabled={deleting}
                          >
                            {deleting ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Indicadores */}
            {stories.length > 1 && (
              <div className="history-times-dots">
                {stories.map((_, idx) => (
                  <button
                    key={idx}
                    className={`history-times-dot ${idx === currentStoryIndex ? 'active' : ''}`}
                    onClick={() => goToStory(idx)}
                    aria-label={`Ir a la historia ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}